"use client";

import { useEffect, useRef, useState } from "react";
import { toast } from "@/components/toast";
import { loadPlacesLibrary } from "@/lib/google-maps";
import type { LocationSelection } from "../lib/types";

type PlaceSuggestion = {
  placeId: string;
  text: string;
  prediction: google.maps.places.PlacePrediction;
};

const DEBOUNCE_MS = 300;

/** Wraps the Places `AutocompleteSuggestion` API: debounced search restricted to Australian
 *  suburbs/postcodes (this field is a location search, not a full street-address picker — see
 *  `features/practioner-application` for that), a billing session token that spans one
 *  query-to-selection cycle, and a `select` step that resolves a prediction to coordinates via
 *  `Place.fetchFields`. Network/API failures are toasted and resolved to an empty/null result
 *  rather than thrown, so a flaky connection never leaves the field stuck. */
export const usePlaceAutocomplete = () => {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const requestIdRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const search = (query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim()) {
      requestIdRef.current++;
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    debounceRef.current = setTimeout(() => void runSearch(query), DEBOUNCE_MS);
  };

  const runSearch = async (query: string) => {
    const requestId = ++requestIdRef.current;

    try {
      const { AutocompleteSuggestion, AutocompleteSessionToken } = await loadPlacesLibrary();
      sessionTokenRef.current ??= new AutocompleteSessionToken();

      const { suggestions: results } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: query,
        sessionToken: sessionTokenRef.current,
        includedRegionCodes: ["au"],
        includedPrimaryTypes: ["(regions)"],
      });

      if (requestId !== requestIdRef.current) return;

      setSuggestions(
        results
          .filter((result) => result.placePrediction !== null)
          .map((result) => ({
            placeId: result.placePrediction!.placeId,
            text: result.placePrediction!.text.text,
            prediction: result.placePrediction!,
          })),
      );
    } catch {
      if (requestId !== requestIdRef.current) return;
      setSuggestions([]);
      toast.error("We couldn't search for that address. Please try again.");
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  };

  const clear = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    requestIdRef.current++;
    setSuggestions([]);
    setIsLoading(false);
  };

  const select = async (suggestion: PlaceSuggestion): Promise<LocationSelection | null> => {
    try {
      const { place } = await suggestion.prediction.toPlace().fetchFields({
        fields: ["location", "formattedAddress"],
      });

      sessionTokenRef.current = null;

      if (!place.location) {
        toast.error("That address doesn't have a precise location. Please choose another.");
        return null;
      }

      return {
        address: place.formattedAddress ?? suggestion.text,
        coordinates: { lat: place.location.lat(), lng: place.location.lng() },
      };
    } catch {
      toast.error("We couldn't fetch that address's details. Please try again.");
      return null;
    }
  };

  return { suggestions, isLoading, search, select, clear };
};
